import type { TFunction } from 'i18next';
import {
  EXCLUSION_REASONS,
  SPECIALTY_COLUMN_PREFIX,
  type ExclusionReason,
  type ImportFileError,
  type ImportRowErrorReason,
  type TechnicianImportField,
  type TechnicianImportValidateResponse,
} from 'api/technicianImportContract';
import { closedListLabel, exclusionReasonLabel, languageLabel, type ClosedListField } from 'pages/AdminTechnician/validation';

const CLOSED_LISTS: Partial<Record<TechnicianImportField, ClosedListField>> = {
  contract_type: 'contract',
  profile_kind: 'profile',
  experience_level: 'level',
  vehicle_type: 'vehicle',
};

/** The Bannissements tab counter: the three groups of CA10. */
export const exclusionCount = ({ counts }: TechnicianImportValidateResponse): number =>
  counts.toExclude + counts.toReinstate + counts.notCreated;

/** Rows with at least one error-severity entry: each is dropped whole; a warning never drops one. */
export const ignoredRowCount = ({ errors }: TechnicianImportValidateResponse): number =>
  new Set(errors.filter((error) => error.severity === 'error').map((error) => error.row)).size;

/** Sorted codes joined by `, ` in a change: shown as + / − (CA9). */
export const MULTI_VALUE_FIELDS: ReadonlySet<TechnicianImportField> = new Set<TechnicianImportField>(['zones_primary', 'zones_secondary', 'expertises']);

/** The template's column headers, literal keys only (the T3 static guard). */
export const importFieldLabel = (t: TFunction, field: TechnicianImportField | string | null): string => {
  if (!field) return '—';
  if (field.startsWith(SPECIALTY_COLUMN_PREFIX)) return t('Specialty: {{code}}', { code: field.slice(SPECIALTY_COLUMN_PREFIX.length) });
  switch (field) {
    case 'id': return t('GPS ID');
    case 'first_name': return t('First name');
    case 'last_name': return t('Last name');
    case 'email': return t('Email');
    case 'phone': return t('Phone');
    case 'contract_type': return t('Contract status');
    case 'company': return t('Company');
    case 'profile_kind': return t('Profile');
    case 'experience_level': return t('Level');
    case 'team_size': return t('Team size');
    case 'vehicle_type': return t('Vehicle');
    case 'lang': return t('Language');
    case 'is_active': return t('Active');
    case 'zones_primary': return t('Primary areas');
    case 'zones_secondary': return t('Secondary areas');
    case 'expertises': return t('Expertises');
    case 'warehouse_address': return t('Warehouse: address');
    case 'warehouse_postal_code': return t('Warehouse: postcode');
    case 'warehouse_city': return t('Warehouse: city');
    case 'warehouse_area_sqm': return t('Warehouse: m²');
    case 'delivery_address': return t('Delivery: address');
    case 'delivery_postal_code': return t('Delivery: postcode');
    case 'delivery_city': return t('Delivery: city');
    case 'delivery_area_sqm': return t('Delivery: m²');
    case 'billing_address': return t('Billing: address');
    case 'billing_postal_code': return t('Billing: postcode');
    case 'billing_city': return t('Billing: city');
    case 'excluded_reason': return t('Ban reason');
    case 'excluded_at': return t('Banned on');
    case 'ops_note': return t('Comment');
    default: return field;
  }
};

/** A cell as the preview shows it: closed lists and yes/no come as codes (PR8), `null` is « — ». */
export const importCellValue = (t: TFunction, field: TechnicianImportField, value: string | null): string => {
  if (value === null || value === '') return '—';
  if (value === 'yes') return t('Yes');
  if (value === 'no') return t('No');
  if (field === 'excluded_reason' && EXCLUSION_REASONS.includes(value as ExclusionReason)) {
    return exclusionReasonLabel(t, value as ExclusionReason);
  }
  const list = CLOSED_LISTS[field];
  if (list) return closedListLabel(t, list, value);
  return field === 'lang' ? languageLabel(t, value) : value;
};

/** A geocoding warning names a whole address (line, postcode, city): the group, not its first cell. */
export const addressGroupLabel = (t: TFunction, field: TechnicianImportField | null): string | null => {
  switch (field) {
    case 'warehouse_address': return t('Address: warehouse');
    case 'delivery_address': return t('Address: reception');
    case 'billing_address': return t('Address: billing');
    default: return null;
  }
};

export const rowErrorReasonLabel = (t: TFunction, reason: ImportRowErrorReason): string => {
  switch (reason) {
    case 'required': return t('Required for an addition');
    case 'invalid_format': return t('Invalid format');
    case 'unknown_referential': return t('Unknown value in the referential');
    case 'ambiguous_label': return t('Ambiguous label: use the code');
    case 'zone_overlap': return t('Area both primary and secondary');
    case 'secondary_without_primary': return t('Secondary area without a primary area');
    case 'duplicate_email': return t('Same email on two rows: both are ignored');
    case 'duplicate_id': return t('Same GPS ID on two rows: both are ignored');
    case 'duplicate_login': return t('Same identifier on two rows: both are ignored');
    case 'email_not_technician': return t('This email belongs to an account that is not a technician');
    case 'email_other_country': return t('This email belongs to a technician of another country');
    case 'id_unknown': return t('Unknown GPS ID');
    case 'id_not_technician': return t('This GPS ID is not a technician');
    case 'id_other_country': return t('This GPS ID is a technician of another country');
    case 'id_email_mismatch': return t('The GPS ID and the e-mail point to different technicians');
    case 'login_taken': return t('Identifier already taken');
    case 'modified_meanwhile': return t('Modified meanwhile, run the analysis again');
    case 'unexpected': return t('Unexpected error while writing');
    case 'address_not_geocodable': return t('Address not found: the row will be imported, but this address cannot be located');
    default: return t('An error occurred');
  }
};

export const fileErrorMessage = (t: TFunction, error: ImportFileError, country: string): string => {
  switch (error.code) {
    case 'not_xlsx': return t('This file is not an Excel workbook (.xlsx).');
    case 'too_large': return t('The file exceeds 5 MB.');
    case 'too_many_rows': return t('The file has more than 1,000 rows. Split it.');
    case 'country_mismatch': return t('This file was prepared for another country than {{country}}.', { country });
    case 'missing_column':
      return t('This file does not follow the template: column “{{column}}” is missing.', { column: importFieldLabel(t, error.column ?? null) });
    case 'unknown_version': return t('This file comes from an unknown version of the template. Download the template again.');
    case 'no_rows': return t('The file contains no technician.');
    case 'zip_bomb': return t('This file is too large once uncompressed.');
    default: return t('An error occurred');
  }
};
